import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { BaseButtonsForm } from '@app/components/common/forms/BaseButtonsForm/BaseButtonsForm';
import { BaseInput } from '@app/components/common/inputs/BaseInput/BaseInput';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { BaseRow } from '@app/components/common/BaseRow/BaseRow';
import { BaseCol } from '@app/components/common/BaseCol/BaseCol';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { BaseImage } from '@app/components/common/BaseImage/BaseImage';

import { useNavigate } from 'react-router-dom';
import { MdModeEdit } from 'react-icons/md';
import { Input, Tooltip, InputRef, DatePicker, Tabs, Space } from 'antd';
import {
  EventRequest,
  storeEvent,
  showEvent,
  editEvent,
  createEvent,
  updateEvent,
  EventApiData,
  Event,
  TypeOfEvent,
  Paper,
} from '@app/api/event.api';
import moment from 'moment';
import dayjs from 'dayjs';
import { TableOutlined, TagOutlined } from '@ant-design/icons';
import TabPane from 'antd/lib/tabs/TabPane';
import { EditableSelectionTable } from '@app/components/tables/EditableSelectionTable/EditableSelectionTable';
import { IoEye } from 'react-icons/io5';
import { v4 as uuidv4 } from 'uuid';
import useApiErrorHandler from '@app/components/Error/ErrorMsgHandling';

interface TypeOfForm {
  type: 'edit' | 'create' | 'view';
}

interface EventFormProps extends TypeOfForm {
  editable: boolean;
  id?: string;
  eventType: TypeOfEvent;
}
const { TextArea } = Input;

const formItemLayout = {
  labelCol: { span: 24 },
  wrapperCol: { span: 24 },
};

interface Ticket {
  id: number | null,
  name: string,
  sale_price: number,
  quantity:number,
  action: string | null;
}

export const EventForm: React.FC<EventFormProps> = ({ type, id, editable = false, eventType }) => {
  const eventId = id ? parseInt(id) : null;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setLoading] = useState(false);
  const [event, setEvent] = useState<Event>();
  const [pageTitle, setPageTitle] = useState<string>('');
  const [slug, setSlug] = useState<string>('');
  const titleInputRef = useRef<InputRef>(null);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [papers, setPapers] = useState<any[]>([]);
  const [paperOptions, setPaperOptions] = useState<Paper[]>([]);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState<string>('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const handleErrorMessages = useApiErrorHandler();
  const handleNameChange = (e: any) => {
    const a = titleInputRef?.current?.input?.value ?? '';
    const b = a.replace(/\s+/g, ' ').trim().replaceAll(' ', '_').toLowerCase();
    form.setFieldValue('slug', b);
  };

  const getPopupContainer = (triggerNode: HTMLElement | null) => {
    return triggerNode || document.body;
  };

  const columns = [
    {
      title: 'Ticket',
      dataIndex: 'name',
    },
    {
      title: 'Price',
      dataIndex: 'sale_price',
    },
    {
      title: 'Available',
      dataIndex: 'quantity',
    },
  ];

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setImageFile(file);

    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setSelectedImage(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (values: EventRequest) => {
    values.image = selectedImage;
    setLoading(true);

    const transformTickets: any[] =
      tickets?.map((ticket) => {
        return ticket?.action !== '4'
          ? {
              id: ticket?.id ?? null,
              name: ticket?.name,
              slug:
                ticket?.name.replace(/\s+/g, ' ').trim().replaceAll(' ', '_').toLowerCase() +
                '_' +
                uuidv4().split('-')[0],
              sale_price: ticket?.sale_price,
              quantity: ticket?.quantity,
              action: ticket?.action ?? '3',
            }
          : {
              id: ticket?.id ?? null,
              action: ticket?.action,
            };
      }) ?? [];

    const paperNames = papers?.map((e) => e.name);
    const paperIds = paperOptions?.filter((e) => paperNames?.includes(e?.name)).map((e) => e.id);

    const formattedValues = {
      ...values,
      type: eventType,
      start_date: dayjs(values.start_date).format('YYYY-MM-DD HH:mm:ss'),
      end_date: dayjs(values.end_date).format('YYYY-MM-DD HH:mm:ss'),
      tickets: transformTickets,
      papers: paperIds,
    };

    console.log(formattedValues);

    if (editable) {
      if (type === 'create') {
        storeEvent(formattedValues)
          .then((res: any) => {
            setLoading(false);
            console.log(res);
            if (eventType === 'CONFERENCE') {
              navigate(`/view-conference/${res?.data?.data?.id}`);
            } else {
              navigate(`/show-event/${res?.data?.data?.id}`);
            }
          })
          .catch((err: any) => {
            setLoading(false);
            handleErrorMessages(err);
            console.error(err);
          });
      } else if (type === 'edit' && eventId) {
        console.log('ssdsds',eventId, formattedValues);
        updateEvent(eventId, formattedValues)
          .then((res: any) => {
            setLoading(false);
            if (eventType === 'CONFERENCE') {
              navigate(`/view-conference/${res?.data?.id}`);
            } else {
              navigate(`/show-event/${res?.data?.id}`);
            }
          })
          .catch((err: any) => {
            setLoading(false);
            handleErrorMessages(err);
            console.error(err);
          });
      }
    }
  };

  const [isFieldsChanged, setFieldsChanged] = useState(false);
  const [form] = BaseButtonsForm.useForm();

  const setApiData = (res: EventApiData) => {
    console.log(res);
    setEvent(res?.event);
    const t:Ticket[] = res?.event?.tickets?.map((e:any) => {return {...e , action : '3'}});
    setTickets(t);
    setImageUrl(res?.event?.image_url ?? '');
    setPaperOptions(res?.paper_options);
    setPapers(res?.event?.papers ??[]);
    form.setFieldsValue({
      title: res?.event?.title,
      slug: res?.event?.slug,
      description: res?.event?.description,
      location: res?.event?.location,
      start_date: res?.event?.start_date != null ? dayjs(res?.event?.start_date) : dayjs(),
      end_date: res?.event?.end_date != null ? dayjs(res?.event?.end_date) : dayjs(),
      tickets: t,
      type: eventType,
    });
  };

  useEffect(() => {
    // Function to fetch data from the API
    setLoading(true);
    if (eventId) {
      if (type === 'view') {
        showEvent(eventType as TypeOfEvent, eventId)
          .then((res: any) => {
            setPageTitle(`${res?.event?.title}`);
            setApiData(res);
            setLoading(false);
          })
          .catch((error) => handleErrorMessages(error));
      } else if (type === 'edit') {
        editEvent(eventType as TypeOfEvent, eventId)
          .then((res: any) => {
            setLoading(false);
            setPageTitle(`${res?.event?.title}`);
            setApiData(res);
            setLoading(false);
          })
          .catch((error) => handleErrorMessages(error));
      }
    } else {
      createEvent()
        .then((res: any) => {
          setLoading(false);
          setPageTitle(`New`);
          setApiData(res);
        })
        .catch((error) => handleErrorMessages(error));
    }
  }, []);

  return (
    <BaseCard
      id="validation form"
      // title={pageTitle}
      title={(eventType === 'EVENT' ? `Event - ` : `Conference - `) + pageTitle || 'New'}
      padding="1.25rem"
      style={{ margin: '80px', marginTop: '0px' }}
      extra={
        <>
          {(type === 'view' && (
            <>
              <Space>
                <Tooltip title={t('common.edit')}>
                  <BaseButton
                    type="text"
                    onClick={() =>
                      navigate(
                         eventType === 'CONFERENCE'
                          ? `/edit-conference/${eventId}`
                          : `/edit-event/${eventId}`,
                      )
                    }
                    icon={<MdModeEdit />}
                  />
                </Tooltip>
              </Space>
            </>
          )) ||
            (type === 'edit' && (
              <>
                <Space>
                  <Tooltip title={t('view')}>
                    <BaseButton
                      type="text"
                      onClick={() => {
                        if (eventType === 'CONFERENCE') {
                          navigate(`/view-conference/${eventId}`);
                        } else {
                          navigate(`/show-event/${eventId}`);
                        }
                      }}
                      icon={<IoEye />}
                    />
                  </Tooltip>
                </Space>
              </>
            ))}
          <Space>
            <Tooltip title={'View all'}>
              <BaseButton
                type="text"
                onClick={() => {
                  if (eventType === 'CONFERENCE') {
                    navigate(`/all-conferences`);
                  } else {
                    navigate(`/all-events`);
                  }
                }}
                icon={<TableOutlined />}
              />
            </Tooltip>
          </Space>
        </>
      }
    >
      {form.isFieldTouched('type')}
      <BaseButtonsForm
        {...formItemLayout}
        name="eventForm"
        onFinish={handleSubmit}
        form={form}
        loading={isLoading}
        isFieldsChanged={isFieldsChanged}
        setFieldsChanged={setFieldsChanged}
      >
        <BaseRow gutter={[16, 16]}>
          <BaseCol xs={24} md={12} lg={12}>
            <BaseButtonsForm.Item name="title" label="Title" rules={[{ required: true, message: 'Title is required' }]}>
              <BaseInput
                ref={titleInputRef}
                placeholder="Enter the Title"
                disabled={!editable}
                onChange={handleNameChange}
              />
            </BaseButtonsForm.Item>
          </BaseCol>
          <BaseCol xs={24} md={12} lg={12}>
            <BaseButtonsForm.Item
              name="slug"
              label="Slug"
              key="slug"
              rules={[{ required: true, message: 'Slug is required' }]}
            >
              <BaseInput placeholder="Enter slug" disabled={!editable} value={slug} />
            </BaseButtonsForm.Item>
          </BaseCol>
        
          <BaseCol xs={24} md={12} lg={12}>
            <BaseButtonsForm.Item
              name="location"
              label="Location"
              key="location"
              rules={[{ required: true, message: 'Location is required' }]}
            >
              <BaseInput placeholder="Enter Location" disabled={!editable} value={slug} />
            </BaseButtonsForm.Item>
          </BaseCol>
        
          <BaseCol xs={24} md={24} lg={24}>
            <BaseButtonsForm.Item name="description" label="Description">
              <TextArea rows={4} placeholder="Enter event description" disabled={!editable} />
            </BaseButtonsForm.Item>
          </BaseCol>
          
        
          <BaseCol xs={24} md={8} lg={12}>
            <BaseButtonsForm.Item label="Start Date & Time" name="start_date">
              <DatePicker
                style={{ display: 'flex', flexGrow: 1 }}
                getPopupContainer={getPopupContainer}
                disabled={!editable}
                showTime
              />
            </BaseButtonsForm.Item>
          </BaseCol>

          <BaseCol xs={6} md={8} lg={12}>
            <BaseButtonsForm.Item label="End Date & Time" name="end_date">
              <DatePicker
                style={{ display: 'flex', flexGrow: 1 }}
                getPopupContainer={getPopupContainer}
                disabled={!editable}
                showTime
              />
            </BaseButtonsForm.Item>
          </BaseCol>
          <BaseCol xs={24} md={8} lg={12}>
            <BaseButtonsForm.Item name="image" label="Thumbnail">
              <input type="file" accept="image/*" onChange={handleFileChange} hidden={!editable} />
              <br hidden={!editable} />
              {selectedImage && 
              <img src={selectedImage}
               alt="thumbnail" style={{ width: '100px', height: '100px' }} />}
              <BaseImage src={imageUrl} preview={true} style={{ width: '100px', height: '100px' }} hidden={!!selectedImage}
              fallback="data:image/png;base64,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">
                {/* {imageUrl && <img src={imageUrl} alt="thumbnail" style={{ width: '100px', height: '100px' }} />} */}
              </BaseImage>
            </BaseButtonsForm.Item>
          </BaseCol>
        </BaseRow>

        <br></br>
        <Tabs defaultActiveKey="1">
          <TabPane
            tab={
              <span>
                <TagOutlined />
                {'Ticket Type List'}
              </span>
            }
            key="1"
          >

            
          <div>
            {isLoading ? (
              <div>Loading...</div>
            ) : (
            <EditableSelectionTable
            loading={isLoading}
              formName={'ticketsForm'}
              formFields={[
                { name: 'name', inputType: 'text', placeholder: 'Ticket', label: 'Ticket', colSpan: 8, type: 'input' },
                {
                  name: 'sale_price',
                  inputType: 'number',
                  placeholder: 'Price',
                  label: 'Price',
                  colSpan: 8,
                  type: 'input',
                },
                {
                  name: 'quantity',
                  inputType: 'number',
                  placeholder: 'Available',
                  label: 'Available',
                  colSpan: 8,
                  type: 'input',
                },
              ]}
              type={type}
              columns={columns}
              orginalItems={tickets}
              updateOriginalItems={setTickets}
              title={''}
              
            />

          )}
          </div>

          </TabPane>
          {eventType === 'CONFERENCE' && (
            <TabPane
              tab={
                <span>
                  <TagOutlined />
                  {'Conference Research Papers'}
                </span>
              }
              key="2"
            >

              <EditableSelectionTable
               loading = {isLoading}
                formName={'papersForm'}
                formFields={[
                  {
                    name: 'name',
                    placeholder: 'Select Paper',
                    label: 'Research Paper',
                    colSpan: 12,
                    type: 'select',
                    dropdownOptions: paperOptions?.map((e) => {
                      return { value: e.name, label: e.name };
                    }),
                  },
                ]}
                type={type}
                columns={[{ title: 'Research Paper', dataIndex: 'name' }]}
                orginalItems={papers}
                updateOriginalItems={setPapers}
                title={''}
                showEdit={false}
              />
            </TabPane>
          )}
        </Tabs>

        <br />
        <br />
        <BaseButtonsForm.Item>
          <BaseButton type="primary" htmlType="submit" loading={isLoading} disabled={!editable}>
            Save Event
          </BaseButton>
        </BaseButtonsForm.Item>
      </BaseButtonsForm>
    </BaseCard>
  );
};

export default EventForm;
